/**
 * One layer of a key among the layers master answers (`?layers=1`, I-2510):
 * what that layer holds itself over what the layers before it give — the
 * view every settings editor works on (the settings list of the shell and
 * of the admin app, the look designer). Pure functions, no Vue.
 */
import type { AccountConfigLayer, EnforcedField } from "./accountConfig";
import {
  applyMergePatch,
  cloneJson,
  isJsonObject,
  mergeJson,
  type JsonObject,
} from "./mergePatch";

/** The subject of a layer, as master names it in a Result's layers. */
export interface AccountConfigLayerRef {
  subjectType: string;
  subjectId: number;
  /** The appearance layer names its appearance (`navy`, `12`). */
  appearance?: string;
}

/** The layer an editor writes: a subject — a built-in appearance by its id (subject id 0). */
export type AccountConfigLayerTarget = AccountConfigLayerRef;

/** What one layer of a key shows: its own document over what the layers before it give. */
export interface AccountConfigLayerView {
  /** The layer's stored document (`{}` without a row). */
  own: JsonObject;
  /** The layers before it merged as master merges them (locked fields of a later layer dropped). */
  inherited: JsonObject;
  /** The layer every inherited leaf value comes from, by dotted path. */
  sources: Record<string, AccountConfigLayerRef>;
  /** Top-level fields a layer before it locks, with that layer (the first one that locks). */
  lockedBy: Record<string, AccountConfigLayerRef>;
  /** Top-level fields the layer locks itself. */
  locks: string[];
  /** The layer's version, 0 without a row (`If-Match` of the next write). */
  version: number;
  /** The layer has a stored row. */
  exists: boolean;
}

/** The chain's order (`account_config_subject_type`): platform → … → device. */
const CHAIN: readonly string[] = [
  "platform",
  "tenant",
  "company",
  "appearance",
  "account",
  "account_profile",
  "account_application",
  "account_device",
];

function rank(subjectType: string): number {
  const at = CHAIN.indexOf(subjectType);
  return at < 0 ? CHAIN.length : at;
}

function isTarget(
  layer: AccountConfigLayerRef,
  target: AccountConfigLayerTarget,
): boolean {
  if (layer.subjectType !== target.subjectType) return false;
  if (target.appearance && layer.appearance)
    return layer.appearance === target.appearance;
  return layer.subjectId === target.subjectId;
}

function refOf(layer: AccountConfigLayer): AccountConfigLayerRef {
  return {
    subjectType: layer.subjectType,
    subjectId: layer.subjectId,
    ...(layer.appearance ? { appearance: layer.appearance } : {}),
  };
}

/** Records where every leaf of `value` comes from; a replaced subtree forgets its old leaves. */
function trace(
  sources: Record<string, AccountConfigLayerRef>,
  value: unknown,
  path: string,
  ref: AccountConfigLayerRef,
): void {
  if (isJsonObject(value)) {
    if (path) delete sources[path];
    for (const [name, child] of Object.entries(value)) {
      if (child !== undefined)
        trace(sources, child, path ? `${path}.${name}` : name, ref);
    }
    return;
  }
  for (const known of Object.keys(sources)) {
    if (known.startsWith(`${path}.`)) delete sources[known];
  }
  sources[path] = ref;
}

/**
 * One layer of a key (`target`) among master's layers: the layer's own
 * document, the layers before it merged (master's merge: deep for objects,
 * arrays and scalars replace, a field an earlier layer locked is dropped), the
 * layer each inherited value comes from, the locks before it and its own.
 * Layers of the target's type that are no target are its ancestors (a
 * tenant's or company's parents) and come before it.
 */
export function accountConfigLayerView(
  layers: readonly AccountConfigLayer[] | null | undefined,
  target: AccountConfigLayerTarget,
): AccountConfigLayerView {
  const list = layers ?? [];
  const targetRank = rank(target.subjectType);
  const at = list.findIndex((layer) => isTarget(layer, target));
  const own = at >= 0 ? list[at]! : null;
  let inherited: JsonObject = {};
  const sources: Record<string, AccountConfigLayerRef> = {};
  const lockedBy: Record<string, AccountConfigLayerRef> = {};
  list.forEach((layer, index) => {
    const layerRank = rank(layer.subjectType);
    const before =
      layerRank < targetRank ||
      (layerRank === targetRank &&
        index !== at &&
        (at < 0 || index < at) &&
        !isTarget(layer, target));
    if (!before) return;
    const ref = refOf(layer);
    const data: JsonObject = {};
    for (const [field, value] of Object.entries(layer.data ?? {})) {
      if (value === undefined || field in lockedBy) continue;
      data[field] = value;
    }
    inherited = mergeJson(inherited, data) as JsonObject;
    trace(sources, data, "", ref);
    for (const field of layer.enforcedFields ?? []) {
      if (!(field in lockedBy)) lockedBy[field] = ref;
    }
  });
  return {
    own: own ? cloneJson(own.data ?? {}) : {},
    inherited,
    sources,
    lockedBy,
    locks: own ? [...(own.enforcedFields ?? [])] : [],
    version: own?.version ?? 0,
    exists: own !== null,
  };
}

/**
 * The layer an inherited value at `path` comes from: the source of the path
 * itself, else of the nearest parent a layer set as a whole (a scalar or
 * array where the path goes deeper). Undefined when no layer before the view's
 * gives a value there.
 */
export function accountConfigLayerSource(
  view: Pick<AccountConfigLayerView, "sources">,
  path: readonly string[],
): AccountConfigLayerRef | undefined {
  for (let depth = path.length; depth > 0; depth -= 1) {
    const source = view.sources[path.slice(0, depth).join(".")];
    if (source) return source;
  }
  return undefined;
}

/**
 * The view with changes on their way to its layer applied to what the layer
 * holds itself (merge patches, oldest first) — what an editor shows until
 * master answered.
 */
export function withPendingLayerPatches(
  view: AccountConfigLayerView,
  patches: readonly JsonObject[],
): AccountConfigLayerView {
  if (!patches.length) return view;
  let own: unknown = view.own;
  for (const patch of patches) own = applyMergePatch(own, patch);
  return { ...view, own: isJsonObject(own) ? own : {} };
}

/**
 * The layer of an appearance: a stored one by its number, a built-in by its
 * catalog id (master gives a built-in the subject id 0 and names it in
 * `appearance`).
 */
export function appearanceLayerTarget(id: string): AccountConfigLayerRef {
  return /^\d+$/.test(id)
    ? { subjectType: "appearance", subjectId: Number(id), appearance: id }
    : { subjectType: "appearance", subjectId: 0, appearance: id };
}

/* ── An appearance's document of a key (I-2510) ──────────────────────────
   What an appearance's layer of a key amounts to — for the look designer's
   preview and for a page arranged for a design (`?design=<id>`), which show
   a key as everybody without values of their own sees it under that
   appearance: the layers before it (platform, tenant, company) with the
   appearance's own document over them, the changes on their way included. A
   field a tenant or company locked keeps their value. */

/** Where an appearance's document of one key comes from: master's answer for it, as the designer read it. */
export interface AppearanceKeySource {
  /** The key's layers (`?appearance=<id>&layers=1`); null while they are not known. */
  layers: readonly AccountConfigLayer[] | null | undefined;
  /** The fields a tenant or company locked (master's `enforced`). */
  enforced?: readonly EnforcedField[];
  /** Changes on their way to the appearance's layer, oldest first (merge patches). */
  pending?: readonly JsonObject[];
  /** What to show while the layers are not known: the session's effective document. */
  fallback?: JsonObject;
}

/** A document with the changes on their way applied, in order. */
export function withPendingPatches(
  doc: JsonObject,
  pending: readonly JsonObject[] | undefined,
): JsonObject {
  let out = doc;
  for (const patch of pending ?? []) {
    const next = applyMergePatch(out, patch);
    out = isJsonObject(next) ? next : {};
  }
  return out;
}

/**
 * A key's document on a layer for everybody below it: the layers before it
 * merged with its own document (the changes on their way applied) — a field
 * locked before it keeps the inherited value. Null while the layers are not
 * known.
 */
export function appearanceLayerDocument(
  target: AccountConfigLayerRef,
  source: AppearanceKeySource,
): JsonObject | null {
  if (!source.layers) return null;
  const view = accountConfigLayerView(source.layers, target);
  const locked = new Set([
    ...Object.keys(view.lockedBy),
    ...(source.enforced ?? []).map((item) => item.field),
  ]);
  const own = withPendingPatches(view.own, source.pending);
  const allowed: JsonObject = {};
  for (const [field, value] of Object.entries(own)) {
    if (!locked.has(field)) allowed[field] = value;
  }
  const merged = applyMergePatch(view.inherited, allowed);
  return isJsonObject(merged) ? merged : {};
}
